import { WEBSITE_DOMAIN } from '@application/constants';
import { isProduction } from '@application/env';
import type { Country, Language } from '@application/types';
import { CountryCode } from '@gql/graphql';
import { addTrailingSlash, useRouter } from '@application/utils';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';
import type { SeoControllerProps } from './types';

export * from './types';

export const SeoController: FC<SeoControllerProps> = (props) => {
  const {
    title = 'Tipaw',
    keywords,
    description,
    canonical,
    thumbnail,
    noIndex = false,
    localeLinks,
  } = props;

  const { countryCode } = useRouter();
  const { pathname } = useLocation();

  const { infoFromCurrentPath, routeLocationDescriptor } = useRouter();

  const isInternationalVersion = countryCode === CountryCode.EU;
  const isPaginationPage = Object.keys(infoFromCurrentPath.search).includes('page');
  const shouldIndex = !isInternationalVersion && !isPaginationPage && noIndex !== true;

  const canonicalUrl = useMemo(() => {
    if (isPaginationPage) {
      return typeof window !== 'undefined' && window?.location.href
        ? `${window.location.origin}${addTrailingSlash(pathname)}`
        : `${WEBSITE_DOMAIN}${addTrailingSlash(pathname)}`;
    }

    return (
      canonical ??
      `${
        typeof window !== 'undefined' && window?.location?.origin
          ? window.location.origin
          : WEBSITE_DOMAIN
      }${addTrailingSlash(pathname)}`
    );
  }, [canonical, isPaginationPage, pathname]);

  const getDefaultLocaleLink = useCallback(
    (count: Country, lang: Language) => {
      const route = routeLocationDescriptor(infoFromCurrentPath.route, {
        newLanguage: lang,
        newCountry: count,
        params: infoFromCurrentPath.params,
        search: infoFromCurrentPath.search,
        ...(infoFromCurrentPath?.state ? { state: infoFromCurrentPath?.state } : {}),
      });

      const path: string = (route as any)?.pathname;
      if (!path) return;

      return `${
        typeof window !== 'undefined' && window?.location?.origin
          ? window.location.origin
          : WEBSITE_DOMAIN
      }${path}`;
    },
    [
      infoFromCurrentPath.params,
      infoFromCurrentPath.route,
      infoFromCurrentPath.search,
      infoFromCurrentPath?.state,
      routeLocationDescriptor,
    ],
  );

  const renderDefaultLocaleLinks = () => {
    const links = [
      <link
        key="fr-be"
        rel="alternate"
        href={getDefaultLocaleLink('be', 'fr')}
        hrefLang="fr-be"
        data-link-fr-be
      />,
      <link
        key="nl-be"
        rel="alternate"
        href={getDefaultLocaleLink('be', 'nl')}
        hrefLang="nl-be"
        data-link-nl-be
      />,
      <link
        key="fr-fr"
        rel="alternate"
        href={getDefaultLocaleLink('fr', 'fr')}
        hrefLang="fr-fr"
        data-link-fr-fr
      />,
      <link
        key="nl-nl"
        rel="alternate"
        href={getDefaultLocaleLink('nl', 'nl')}
        hrefLang="nl-nl"
        data-link-nl-nl
      />,
      <link
        key="de-de"
        rel="alternate"
        href={getDefaultLocaleLink('de', 'de')}
        hrefLang="de-de"
        data-link-de-de
      />,
      <link
        key="fr-ch"
        rel="alternate"
        href={getDefaultLocaleLink('ch', 'fr')}
        hrefLang="fr-ch"
        data-link-fr-ch
      />,
      <link
        key="de-ch"
        rel="alternate"
        href={getDefaultLocaleLink('ch', 'de')}
        hrefLang="de-ch"
        data-link-de-ch
      />,
    ];

    return links;
  };

  const renderLocaleLinks = () => {
    const links = [];
    if (localeLinks?.be) {
      links.push(
        <link
          key="fr-be"
          rel="alternate"
          href={localeLinks.be.fr}
          hrefLang="fr-be"
          data-link-fr-be
        />,
      );
      links.push(
        <link
          key="nl-be"
          rel="alternate"
          href={localeLinks.be.nl}
          hrefLang="nl-be"
          data-link-nl-be
        />,
      );
    }

    if (localeLinks?.fr) {
      links.push(
        <link
          key="fr-fr"
          rel="alternate"
          href={localeLinks.fr.fr}
          hrefLang="fr-fr"
          data-link-fr-fr
        />,
      );
    }

    if (localeLinks?.nl) {
      links.push(
        <link
          key="nl-nl"
          rel="alternate"
          href={localeLinks.nl.nl}
          hrefLang="nl-nl"
          data-link-nl-nl
        />,
      );
    }
    if (localeLinks?.de) {
      links.push(
        <link
          key="de-de"
          rel="alternate"
          href={localeLinks.de.de}
          hrefLang="de-de"
          data-link-de-de
        />,
      );
    }
    if (localeLinks?.ch) {
      links.push(
        <link
          key="fr-ch"
          rel="alternate"
          href={localeLinks.ch.fr}
          hrefLang="fr-ch"
          data-link-fr-ch
        />,
      );
      links.push(
        <link
          key="de-ch"
          rel="alternate"
          href={localeLinks.ch.de}
          hrefLang="de-ch"
          data-link-de-ch
        />,
      );
    }

    return links;
  };

  // TC-1284 - in case a translation key is not found, we do not want to display a missing translation
  const formattedTitle = title?.includes('.seo.') ? 'Tipaw' : title;

  return (
    <Helmet>
      <title>{formattedTitle}</title>
      {!!description && <meta name="description" content={description} />}
      {!!keywords && <meta name="keywords" content={keywords} />}

      {!!thumbnail && <meta property="og:image" content={thumbnail} data-rh="true" />}
      {!!thumbnail && <meta property="og:image:width" content="1200" data-rh="true" />}
      {!!thumbnail && <meta property="og:image:height" content="630" data-rh="true" />}
      {!!thumbnail && <meta property="og:image:alt" content={title} data-rh="true" />}

      {canonicalUrl && <meta property="og:url" content={canonicalUrl} data-rh="true" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} data-rh="true" />}

      {!!formattedTitle && <meta property="og:title" content={formattedTitle} />}
      {!!description && <meta property="og:description" content={description} />}

      {localeLinks ? renderLocaleLinks() : renderDefaultLocaleLinks()}

      {shouldIndex ? (
        <meta name="robots" content="index, follow" />
      ) : (
        <meta name="robots" content="noindex, follow" />
      )}

      {!isProduction() && <meta name="robots" content="noindex" />}
      {!isProduction() && <meta name="googlebot" content="noindex" />}
    </Helmet>
  );
};
